import type React from 'react';

import Typography, { type TypographyProps } from '@mui/material/Typography';
import classnames from 'classnames';

import { useStyles } from './styles';

interface TableHeadingProps extends TypographyProps {
  count?: number | string;
  props?: any;
}

interface Props extends Omit<TypographyProps, 'component'> {
  bold?: boolean;
  className?: TypographyProps['className'];
  component?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | React.ElementType;
}

export const TableHeading: React.FC<React.PropsWithChildren<TableHeadingProps>> = ({ children, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classes.tableHeading}>
      {children}
    </Typography>
  );
};

export const FormLabelText: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.formLabelText, className)}>
      {children}
    </Typography>
  );
};

export const FormLabelTextLarge: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.formLabelTextLarge, className)}>
      {children}
    </Typography>
  );
};

export const FormSecondaryText: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.formSecondaryText, className)}>
      {children}
    </Typography>
  );
};

export const H1: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.h1, className)}>
      {children}
    </Typography>
  );
};

export const H2: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.h2, className)}>
      {children}
    </Typography>
  );
};

export const H3: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.h3, className)}>
      {children}
    </Typography>
  );
};

export const H4: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.h4, className)}>
      {children}
    </Typography>
  );
};

export const H5: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.h5, className)}>
      {children}
    </Typography>
  );
};

export const BodySmall: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.bodySmall, className)}>
      {children}
    </Typography>
  );
};

export const BodySmallLight: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.bodySmallLight, className)}>
      {children}
    </Typography>
  );
};

export const BodySmallBold: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.bodySmallBold, className)}>
      {children}
    </Typography>
  );
};

export const BodyRegular: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.bodyRegular, className)}>
      {children}
    </Typography>
  );
};

export const BodyRegularLight: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.bodyRegularLight, className)}>
      {children}
    </Typography>
  );
};

export const BodyRegularBold: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.bodyRegularBold, className)}>
      {children}
    </Typography>
  );
};

export const BodyLarge: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.bodyLarge, className)}>
      {children}
    </Typography>
  );
};

export const BodyLargeBold: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.bodyLargeBold, className)}>
      {children}
    </Typography>
  );
};

export const BodyLargeLight: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.bodyLargeLight, className)}>
      {children}
    </Typography>
  );
};

export const BodyLargeDark: React.FC<React.PropsWithChildren<Props>> = ({ children, className, bold, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.bodyLargeDark, className, bold && classes.bold)}>
      {children}
    </Typography>
  );
};

export const BodyXLarge: React.FC<React.PropsWithChildren<Props>> = ({ children, className, bold, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.bodyXLarge, className, bold && classes.bold)}>
      {children}
    </Typography>
  );
};

export const SelectItemLabel: React.FC<React.PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { classes } = useStyles();
  return (
    <Typography {...props} className={classnames(classes.selectLabel, className)}>
      {children}
    </Typography>
  );
};
